/**
 * shipWalkthrough: the REACTIVE READ MODEL of the ship-walkthrough ledger
 * (W4-F2.10, Addendum 5 rule 4).
 *
 * WHAT IT IS NOT. It is not a second ledger. The record lives in
 * `components/Dashboard/Completion/endingState.js`, under the one
 * `(learner, challenge)` key that module already owns, and that is the only
 * place it is ever written durably. This slice is the store-shaped mirror of it,
 * and it exists for one mechanical reason: the Ship gate is decided inside
 * `deriveJourneyStep`, which is pure and reached through `selectJourneyInputs`,
 * a `createSelector`. A selector that read `localStorage` would not recompute
 * when the ledger changed, so the pass that follows the ship event would still
 * see the learner short of their own walkthrough and emit no `ADVANCE_CAPSULE`
 * at all. The input has to be in the store for the derivation to see it move.
 *
 * MIRRORING A `localStorage` FLAG INTO A SLICE IS THE HOUSE PATTERN.
 * `keyshawn.disclosureAcknowledged` is the same shape for the same reason.
 *
 * THERE IS EXACTLY ONE WRITE PATH, and that is what keeps the two from
 * drifting: `recordShipWalkthrough` below writes the ledger and then dispatches
 * the mirror, in that order, inside one thunk. No caller writes either half on
 * its own. `hydrateShipWalkthroughs` is the read half, run when the scope is
 * established or changes.
 *
 * IT IS SESSION-ONLY IN REDUX AND DELIBERATELY ABSENT FROM THE PERSIST
 * WHITELIST (`store/index.js`). The durable copy is the ledger; a second
 * persisted copy would be a second answer that could come back from storage
 * disagreeing with the first.
 *
 * THE SCOPE KEY IS THE LEARNER AND THE CHALLENGE, AND IT IS CHECKED BY THE
 * READER. A shared school device switches accounts with the same capsule
 * selected (the defect `credentialClearance` was re-keyed for in Wave 3 round
 * 4), and a mirror that answered for whoever hydrated it last would hand
 * learner B learner A's walkthrough and skip B's Ship step. So the held scope
 * travels with the held ids and `selectJourneyInputs` compares it.
 *
 * NOTHING PERSONAL IS HELD: a key of two ids and a list of capsule ids.
 */
import { createSlice } from "@reduxjs/toolkit";

import {
  clearMovedForward as clearMovedForwardMark,
  movedForwardCapsuleId as readMovedForward,
  shipWalkthroughs as readShipWalkthroughs,
  recordMovedForward as writeMovedForward,
  recordShipWalkthrough as writeShipWalkthrough,
} from "../../components/Dashboard/Completion/endingState";

/** The one key shape. Empty ids never produce a key, exactly as the ledger. */
export const shipWalkthroughScopeKey = ({ userId, challengeId } = {}) =>
  userId && challengeId ? `${userId}:${challengeId}` : null;

const initialState = {
  /** The `(learner, challenge)` the held ids belong to, or null. */
  scopeKey: null,
  /** Capsule ids, coerced to numbers by the ledger before they arrive here. */
  capsuleIds: [],
  /**
   * THE CAPSULE THIS LEARNER WAS CARRIED PAST PLAN AND WORK ON, or null
   * (W4-F2.11 fix batch, Codex MINOR 1).
   *
   * IT IS IN THIS SLICE AND NOT A SECOND ONE FOR THE REASON THE HEADER GIVES.
   * It is the mirror of another field of the same `(learner, challenge)` ledger
   * record, read by the same derivation through the same `createSelector`, and
   * a second slice would be a second scope key to forget to compare.
   */
  movedForwardCapsuleId: null,
  /**
   * HAS THE LEDGER BEEN READ AT ALL IN THIS PAGE LIFE? (fix batch, MINOR 3.)
   *
   * An empty mirror has two meanings and the gate cannot tell them apart from
   * `capsuleIds` alone: "this learner has walked through nothing" and "nobody
   * has asked yet". The second is the state of the FIRST render, because the
   * hydration is a passive effect and `selectNextAction` is evaluated during
   * render, so a learner with a recorded walkthrough had one committed frame
   * parked at their own Ship step before the effect landed.
   *
   * `selectJourneyInputs` reads this into `ready`, and an unready decision is
   * `JOURNEY_STEP.NONE` with no side effects, which is the same nothing the
   * page already renders while its capsules are loading. It is a FLAG AND NOT
   * A SCOPE COMPARISON because an incomplete scope hydrates the empty answer
   * on purpose (see below): the question here is only whether the read has
   * happened, and the scope is checked separately by the reader.
   */
  hasHydrated: false,
};

const shipWalkthroughSlice = createSlice({
  name: "shipWalkthrough",
  initialState,
  reducers: {
    /**
     * The ledger's answer for a scope, replacing whatever was held.
     *
     * IT IS THE ONLY WRITER, for the record and for the read alike (fix batch,
     * MINOR 4). The slice used to carry a second reducer that APPENDED the
     * capsule a record had asked for; that is a claim about the ledger rather
     * than a copy of it, and on a browser that refused the write the two
     * disagreed. Both callers now hand over the list the ledger effectively
     * holds, so there is one shape of answer and no arithmetic here to get
     * wrong. A record for a different scope replaces rather than merges, which
     * is the shared-device rule this slice's header names, and it comes for
     * free from replacing.
     */
    hydrated: (state, action) => {
      const { scopeKey, capsuleIds, movedForwardCapsuleId } =
        action.payload || {};
      state.scopeKey = scopeKey ?? null;
      state.capsuleIds = Array.isArray(capsuleIds) ? capsuleIds : [];
      state.movedForwardCapsuleId =
        movedForwardCapsuleId === undefined ? null : movedForwardCapsuleId;
      state.hasHydrated = true;
    },
  },
});

/**
 * NO `clear` ACTION, AND THAT IS NOT AN OMISSION. Two things already do the job
 * and a third would be a branch nothing calls: the root reducer resets every
 * slice on `LOGOUT`, and the hydration below runs on every scope change and
 * REPLACES what is held, so leaving a learner or a challenge is already a
 * teardown. The sibling slices carry one; none of their callers use it.
 */
export const { hydrated } = shipWalkthroughSlice.actions;

/**
 * Read the ledger for a scope into the store.
 *
 * Run when the scope is ESTABLISHED and again whenever it changes, so a learner
 * switching account or challenge on one tab never inherits the prior scope's
 * walkthroughs. An incomplete scope hydrates the empty answer rather than
 * leaving the previous one in place, which is the conservative direction: the
 * learner is offered their own Ship step rather than skipped past it.
 */
export const hydrateShipWalkthroughs =
  ({ userId, challengeId } = {}) =>
  (dispatch) => {
    const scopeKey = shipWalkthroughScopeKey({ userId, challengeId });
    dispatch(
      hydrated({
        scopeKey,
        capsuleIds: scopeKey ? readShipWalkthroughs(userId, challengeId) : [],
        movedForwardCapsuleId: scopeKey
          ? readMovedForward(userId, challengeId)
          : null,
      }),
    );
  };

/**
 * THE SHIP EVENT'S OWN RECORD. The durable write first, the mirror second.
 *
 * THE ORDER IS NOT COSMETIC. The mirror is what the next derivation pass reads,
 * so dispatching it first would open a window in which the rail had advanced on
 * a fact no reload could reproduce. The ledger write is synchronous and its
 * failure mode is already an in-memory fallback, so there is no case in which
 * the mirror moves and the ledger holds nothing at all.
 */
export const recordShipWalkthrough =
  ({ userId, challengeId, capsuleId } = {}) =>
  (dispatch) => {
    const scopeKey = shipWalkthroughScopeKey({ userId, challengeId });
    if (!scopeKey || capsuleId === null || capsuleId === undefined) return;
    /* THE MIRROR IS THE WRITE'S OWN ANSWER (fix batch, MINOR 4), not the
       capsule this call asked for. On a browser that refuses `setItem` those
       two are the same value; on one that refuses it with a record already
       stored they were not, and the mirror then held a walkthrough the next
       hydration could not reproduce. */
    const capsuleIds = writeShipWalkthrough(userId, challengeId, capsuleId);
    /* AND THE CARRY MARK FOR THIS LEVEL IS SPENT. A learner past Ship is past
       Check-in, so nothing the learner can see changes; what changes is that
       the mark does not outlive the level it describes. `clearMovedForward`
       refuses to clear a mark naming a different capsule. */
    const movedForwardCapsuleId = clearMovedForwardMark(
      userId,
      challengeId,
      capsuleId,
    );
    dispatch(hydrated({ scopeKey, capsuleIds, movedForwardCapsuleId }));
  };

/**
 * THE CARRY, RECORDED WHERE IT HAPPENS (W4-F2.11 fix batch, Codex MINOR 1).
 *
 * The durable write first and the mirror second, for the reason the walkthrough
 * thunk above gives: the mirror is what the next derivation pass reads, so
 * dispatching it first would open a window in which the Check-in line made a
 * claim no reload could reproduce.
 *
 * IT IS IDEMPOTENT AT THE REDUCER. The caller is a render effect keyed on a
 * decision that is re-derived on every commit, and the hydration it dispatches
 * is an input to that same decision, so a reducer that wrote a new array on
 * every call would be a loop. `hydrated` replaces, and the effect that calls
 * this guards on the value it last wrote.
 */
export const recordMovedForward =
  ({ userId, challengeId, capsuleId } = {}) =>
  (dispatch) => {
    const scopeKey = shipWalkthroughScopeKey({ userId, challengeId });
    if (!scopeKey || capsuleId === null || capsuleId === undefined) return;
    dispatch(
      hydrated({
        scopeKey,
        capsuleIds: readShipWalkthroughs(userId, challengeId),
        movedForwardCapsuleId: writeMovedForward(
          userId,
          challengeId,
          capsuleId,
        ),
      }),
    );
  };

export default shipWalkthroughSlice.reducer;
